/**
 * Shared blog template v2 chrome.
 * Markup and class names match website-amitasoft/src/design-preview/blog-v2/article.html.
 * Sidebar copy is verbatim from the migration brief.
 */
import { getPostMetadata } from './blogPostMetadata';

export const SIDEBAR_VARIANTS = {
  TaxFlo: {
    heading: 'Tax workflow software for CPA firms',
    body: 'TaxFlo runs the engagement rather than the folder. Guided intake, prior-year reuse, and a live view of what is still outstanding.',
    featuresHref: '/products/taxflo',
  },
  CliQloan: {
    heading: 'Mortgage workflow automation',
    body: 'CliQloan connects document collection, verification, and the loan file so approvals stop waiting on paperwork.',
    featuresHref: '/products/cliqloan',
  },
  SafeVault: {
    heading: 'Secure document management',
    body: 'SafeVault protects sensitive personal, financial, and family documents with encrypted handling, secure access controls, and intelligent organization.',
    featuresHref: '/products/safevault',
  },
};

export function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

export function requirePostMetadata(slug) {
  return getPostMetadata(slug);
}

export function resolveRelatedPosts(relatedSlugs, getPostBySlug) {
  return (relatedSlugs || [])
    .map((slug) => getPostBySlug(slug))
    .filter((post) => post && post.slug && !post.redirectTo);
}

/**
 * FAQ questions must match FAQPage schema and the visible heading.
 * Strip leftover markdown heading marks and wrapping emphasis so
 * "### How long should I keep tax returns?" never reaches the page.
 */
export function cleanFaqQuestion(value) {
  let text = String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  for (let i = 0; i < 3; i += 1) {
    const unwrapped = text
      .replace(/^\*+([\s\S]+?)\*+$/s, '$1')
      .replace(/^_([\s\S]+?)_$/s, '$1')
      .trim();
    if (unwrapped === text) break;
    text = unwrapped;
  }
  text = text.replace(/^#{1,6}\s+/, '').trim();
  text = text.replace(/^\*+|\*+$/g, '').trim();
  text = text.replace(/^#{1,6}\s+/, '').trim();
  return text;
}

export function wrapFaqAsAccordion(html) {
  const source = String(html || '');
  const headingRe = /<h2\b[^>]*>\s*Frequently asked questions\s*<\/h2>/i;
  const headingMatch = source.match(headingRe);
  if (!headingMatch) return source;

  const start = headingMatch.index + headingMatch[0].length;
  const rest = source.slice(start);
  const nextH2 = rest.search(/<h2\b/i);
  const faqBlock = nextH2 === -1 ? rest : rest.slice(0, nextH2);
  const after = nextH2 === -1 ? '' : rest.slice(nextH2);

  const items = [];
  const pairRe = /<(h3|p)\b[^>]*>([\s\S]*?)<\/\1>\s*<p\b[^>]*>([\s\S]*?)<\/p>/gi;
  let pair;
  while ((pair = pairRe.exec(faqBlock)) !== null) {
    const question = cleanFaqQuestion(pair[2]);
    if (!question) continue;
    if (pair[1].toLowerCase() === 'p' && !/[#?]/.test(pair[2]) && !/<strong|<em/i.test(pair[2])) {
      continue;
    }
    items.push({ question: escapeHtml(question), answer: pair[3].trim() });
  }
  if (!items.length) return source;

  const accordion = items
    .map((item, index) => {
      const open = index === 0 ? ' open' : '';
      return `<details class="faq-item border-b border-surface-variant"${open}>
<summary class="faq-summary flex items-start justify-between gap-4 cursor-pointer list-none py-5">
<h3 class="font-headline-md text-headline-md text-primary !mt-0 !mb-0 text-lg">${item.question}</h3>
<span class="faq-chevron" aria-hidden="true"></span>
</summary>
<div class="faq-answer pb-5 pr-10"><p>${item.answer}</p></div>
</details>`;
    })
    .join('\n');

  return `${source.slice(0, headingMatch.index)}${headingMatch[0]}
<div class="faq-accordion mt-8 border-t border-surface-variant">${accordion}</div>
${after}`;
}

/**
 * Shared Blog V2 figure pattern.
 *
 * Desktop (≥1024): keep the authored PNG.
 * Narrow viewports: hide the wide raster and show a stacked HTML reflow
 * so labels stay readable without horizontal panning.
 *
 * Future posts: add a recipe keyed by image filename, or author a
 * `.blog-v2-figure-reflow` sibling inside the <figure>.
 */
const FIGURE_REFLOW_RECIPES = {
  'img1-where-follow-ups-start.png': `<div class="blog-v2-figure-reflow" data-figure="follow-ups-start">
<p class="blog-v2-fig-title">Where tax season follow-ups start</p>
<p class="blog-v2-fig-sub">Four intake failures account for most of the chasing between January and April.</p>
<ol class="blog-v2-fig-cards">
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">1</span>
<p class="blog-v2-fig-card-title">Ambiguous request</p>
<p class="blog-v2-fig-card-body">“Prior year documents” gets last year’s return, not the K-1s you needed.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">2</span>
<p class="blog-v2-fig-card-title">Four channels</p>
<p class="blog-v2-fig-card-body">Email, text, portal, shared drive. Nothing lands in one place.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">3</span>
<p class="blog-v2-fig-card-title">No visible status</p>
<p class="blog-v2-fig-card-body">The preparer knows what is outstanding. The client does not.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">4</span>
<p class="blog-v2-fig-card-title">Wrong recipient</p>
<p class="blog-v2-fig-card-body">The reminder goes to the spouse who never handles the paperwork.</p>
</li>
</ol>
<p class="blog-v2-fig-foot">None of these describe a difficult client. They describe an intake process built in a hurry.</p>
</div>`,

  'img2-request-line-comparison.png': `<div class="blog-v2-figure-reflow" data-figure="request-line">
<p class="blog-v2-fig-title">One request line, two outcomes</p>
<p class="blog-v2-fig-sub">Name the form, name the year, say where the client will find it</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">Vague</p>
<p class="blog-v2-fig-quote">“Please send your mortgage documents”</p>
<ul class="blog-v2-fig-outcomes">
<li>Client sends the 2024 form</li>
<li>Client sends the closing disclosure instead</li>
<li>Two more emails to get the right one</li>
<li>The file stalls in the meantime</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">Specific</p>
<p class="blog-v2-fig-quote">“Your 2025 Form 1098 from the same servicer as last year. It is under Tax Documents in your online banking.”</p>
<ul class="blog-v2-fig-outcomes">
<li>Client sends the right document first time</li>
<li>No follow-up required</li>
</ul>
</div>
</div>
</div>`,

  'img3-follow-up-cadence.png': `<div class="blog-v2-figure-reflow" data-figure="follow-up-cadence">
<p class="blog-v2-fig-title">A follow-up cadence a firm can hold</p>
<p class="blog-v2-fig-sub">Decide it in November. Assign it to one person.</p>
<ol class="blog-v2-fig-timeline">
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Day 0</p>
<p class="blog-v2-fig-step-body">Initial request with the full list</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Day 7</p>
<p class="blog-v2-fig-step-body">Reminder showing only what is outstanding</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Day 14</p>
<p class="blog-v2-fig-step-body">Reminder plus the effect on the filing date</p>
</li>
<li class="blog-v2-fig-step blog-v2-fig-step--alert">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Day 21</p>
<p class="blog-v2-fig-step-body">A phone call, not another email</p>
</li>
</ol>
<p class="blog-v2-fig-foot">A client who ignored two emails will ignore a third.</p>
</div>`,

  't5-img1-same-headcount.png': `<div class="blog-v2-figure-reflow" data-figure="same-headcount">
<p class="blog-v2-fig-title">Same headcount, different season</p>
<p class="blog-v2-fig-sub">Six preparers each. The difference is how many returns can be worked on today.</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">Firm A</p>
<ul class="blog-v2-fig-outcomes">
<li>11 startable</li>
<li>29 waiting on documents</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">Firm B</p>
<ul class="blog-v2-fig-outcomes">
<li>31 startable</li>
<li>9 waiting on documents</li>
</ul>
</div>
</div>
<p class="blog-v2-fig-foot">Adding a seventh preparer to Firm A buys a seventh person waiting on documents.</p>
</div>`,

  't5-img2-eighty-percent-return.png': `<div class="blog-v2-figure-reflow" data-figure="eighty-percent-return">
<p class="blog-v2-fig-title">The eighty percent return</p>
<p class="blog-v2-fig-sub">Time already spent, nothing delivered, and a re-read waiting at the end.</p>
<ol class="blog-v2-fig-cards">
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">80%</span>
<p class="blog-v2-fig-card-title">80% prepared</p>
<p class="blog-v2-fig-card-body">Preparer time already spent</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">—</span>
<p class="blog-v2-fig-card-title">Blocked — one missing K-1</p>
<p class="blog-v2-fig-card-body">A queue slot held, producing nothing</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">→</span>
<p class="blog-v2-fig-card-title">When it unblocks</p>
<p class="blog-v2-fig-card-body">A full re-read when it unblocks</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Few firms count the re-read. It is one of the largest silent costs in the season.</p>
</div>`,

  't5-img3-review-bottleneck.png': `<div class="blog-v2-figure-reflow" data-figure="review-bottleneck">
<p class="blog-v2-fig-title">Adding preparers moves the queue, it does not remove it</p>
<p class="blog-v2-fig-sub">Preparation capacity can be bought. Review capacity is attached to a person.</p>
<ol class="blog-v2-fig-cards">
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">6</span>
<p class="blog-v2-fig-card-title">Preparer 1–6</p>
<p class="blog-v2-fig-card-body">Preparation capacity can be bought.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">1</span>
<p class="blog-v2-fig-card-title">Review</p>
<p class="blog-v2-fig-card-body">One partner, also running clients</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">→</span>
<p class="blog-v2-fig-card-title">Filed returns</p>
<p class="blog-v2-fig-card-body">Throughput is set here, not at the desks on the left.</p>
</li>
</ol>
<p class="blog-v2-fig-foot">If review is the constraint, every partner hour spent on preparer work costs a return.</p>
</div>`,

  't6-img2-sent-vs-received.png': `<div class="blog-v2-figure-reflow" data-figure="sent-vs-received">
<p class="blog-v2-fig-title">What you send decides what you receive</p>
<p class="blog-v2-fig-sub">A client who does not know where things stand has one available action: asking you.</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">Firm A — 1 planned message</p>
<ul class="blog-v2-fig-outcomes">
<li>Sent: short</li>
<li>Received: heavy inbound</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">Firm B — 5 planned messages</p>
<ul class="blog-v2-fig-outcomes">
<li>Sent: long</li>
<li>Received: light inbound</li>
</ul>
</div>
</div>
<p class="blog-v2-fig-foot">Same firms, same clients. The difference is scheduled, not responsive.</p>
</div>`,

  't6-img1-six-messages.png': `<div class="blog-v2-figure-reflow" data-figure="six-messages">
<p class="blog-v2-fig-title">The six messages a filing season needs</p>
<p class="blog-v2-fig-sub">Five can be written once and reused. Only the extension conversation needs a partner.</p>
<ol class="blog-v2-fig-timeline">
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">1 · Mid-Dec</p>
<p class="blog-v2-fig-step-body">Engagement opener</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">2 · Day 7</p>
<p class="blog-v2-fig-step-body">Outstanding items nudge</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">3 · Day 14</p>
<p class="blog-v2-fig-step-body">Second nudge + date impact</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">4 · File complete</p>
<p class="blog-v2-fig-step-body">We have started your return</p>
</li>
<li class="blog-v2-fig-step blog-v2-fig-step--alert">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">5 · Early March</p>
<p class="blog-v2-fig-step-body">Extension conversation — partner writes</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">6 · On completion</p>
<p class="blog-v2-fig-step-body">Delivery + next steps</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Messages 1 to 4 should leave the partner's desk entirely.</p>
</div>`,

  't2-img1-two-folder-model.png': `<div class="blog-v2-figure-reflow" data-figure="two-folder-model">
<p class="blog-v2-fig-title">Two folders, not one</p>
<p class="blog-v2-fig-sub">Year folders expire together. The permanent folder never does. Keeping them apart is what makes disposal safe.</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">Tax 2025 — expires with the return</p>
<ul class="blog-v2-fig-outcomes">
<li>01 Income</li>
<li>02 Deductions</li>
<li>03 Investments</li>
<li>04 Business</li>
<li>05 Payments</li>
<li>06 Filed return</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">Permanent — never expires</p>
<ul class="blog-v2-fig-outcomes">
<li>Home - Maple St</li>
<li>Investments</li>
<li>Retirement basis</li>
<li>Carryforwards</li>
<li>Entity</li>
</ul>
</div>
</div>
<p class="blog-v2-fig-foot">When a tax year's period expires you delete the whole folder, because nothing long-lived was ever in it.</p>
</div>`,

  't2-img2-file-naming.png': `<div class="blog-v2-figure-reflow" data-figure="file-naming">
<p class="blog-v2-fig-title">Name files so the sort order does the work</p>
<p class="blog-v2-fig-sub">Year, then form or type, then source. Alphabetical sorting then groups every 1099 on its own.</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">BEFORE</p>
<ul class="blog-v2-fig-outcomes">
<li>Scan_20260114_0003.pdf</li>
<li>document (2).pdf</li>
<li>IMG_4471.HEIC</li>
<li>Untitled.pdf</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">AFTER</p>
<ul class="blog-v2-fig-outcomes">
<li>2025_1098_Wells-Fargo.pdf</li>
<li>2025_1099-DIV_Vanguard.pdf</li>
<li>2025_1099-INT_Chase.pdf</li>
<li>2025_W2_Acme-Corp.pdf</li>
</ul>
</div>
</div>
<p class="blog-v2-fig-foot">Did the second savings account 1099 arrive? becomes a four-second question.</p>
</div>`,

  't2-img3-file-on-arrival.png': `<div class="blog-v2-figure-reflow" data-figure="file-on-arrival">
<p class="blog-v2-fig-title">File on arrival, not at filing time</p>
<p class="blog-v2-fig-sub">The decision is easy while the context is fresh. In March, it is forty untitled PDFs.</p>
<div class="blog-v2-fig-compare">
<div class="blog-v2-fig-panel blog-v2-fig-panel--vague">
<p class="blog-v2-fig-panel-label">Batched in March</p>
<ul class="blog-v2-fig-outcomes">
<li>one long session</li>
<li>40 decisions, no context</li>
</ul>
</div>
<div class="blog-v2-fig-flow" aria-hidden="true"></div>
<div class="blog-v2-fig-panel blog-v2-fig-panel--specific">
<p class="blog-v2-fig-panel-label">Filed on arrival</p>
<ul class="blog-v2-fig-outcomes">
<li>20 seconds each</li>
<li>context still fresh</li>
</ul>
</div>
</div>
<p class="blog-v2-fig-foot">If putting a document away takes a decision, the system will not survive a busy February.</p>
</div>`,

  't1-img1-clock-starts-at-filing.png': `<div class="blog-v2-figure-reflow" data-figure="clock-starts-at-filing">
<p class="blog-v2-fig-title">The three years runs from your filing date</p>
<p class="blog-v2-fig-sub">Not from the end of the tax year, which is where most people miscount.</p>
<ol class="blog-v2-fig-timeline">
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">31 Dec 2025</p>
<p class="blog-v2-fig-step-body">Tax year ends</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">10 Apr 2026</p>
<p class="blog-v2-fig-step-body">You file</p>
</li>
<li class="blog-v2-fig-step blog-v2-fig-step--alert">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Dec 2028</p>
<p class="blog-v2-fig-step-body">Where people think it ends</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Apr 2029</p>
<p class="blog-v2-fig-step-body">Period expires — 3 years from filing</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Filing early does not help. A return filed before the due date is treated as filed on the due date.</p>
</div>`,

  't1-img2-retention-by-situation.png': `<div class="blog-v2-figure-reflow" data-figure="retention-by-situation">
<p class="blog-v2-fig-title">How long, by situation</p>
<p class="blog-v2-fig-sub">The IRS sets the period by what happened on the return, not by document type.</p>
<ol class="blog-v2-fig-cards">
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">3</span>
<p class="blog-v2-fig-card-title">The ordinary case</p>
<p class="blog-v2-fig-card-body">3 years</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">4</span>
<p class="blog-v2-fig-card-title">Employment tax records</p>
<p class="blog-v2-fig-card-body">4 years</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">6</span>
<p class="blog-v2-fig-card-title">Under-reported income over 25%</p>
<p class="blog-v2-fig-card-body">6 years</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">7</span>
<p class="blog-v2-fig-card-title">Worthless securities or bad debt</p>
<p class="blog-v2-fig-card-body">7 years</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">∞</span>
<p class="blog-v2-fig-card-title">No return filed, or fraudulent return</p>
<p class="blog-v2-fig-card-body">No limit</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Periods run from the filing date. Keep the filed returns themselves indefinitely.</p>
</div>`,

  't1-img3-basis-records.png': `<div class="blog-v2-figure-reflow" data-figure="basis-records">
<p class="blog-v2-fig-title">The records that outlive the three-year rule</p>
<p class="blog-v2-fig-sub">Basis records stay relevant until three years after you sell, not after you buy.</p>
<ol class="blog-v2-fig-timeline">
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Purchase</p>
<p class="blog-v2-fig-step-body">You own the home · 25 years of improvement receipts</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Sale</p>
<p class="blog-v2-fig-step-body">+ 3 years</p>
</li>
<li class="blog-v2-fig-step">
<span class="blog-v2-fig-step-dot" aria-hidden="true"></span>
<p class="blog-v2-fig-step-day">Records can go</p>
<p class="blog-v2-fig-step-body">New roof, addition, replaced HVAC. Each one raises your basis, but only if you can evidence it.</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Same rule for stock, rental property, business equipment, and anything inherited or gifted.</p>
</div>`,

  't6-img3-who-sends-what.png': `<div class="blog-v2-figure-reflow" data-figure="who-sends-what">
<p class="blog-v2-fig-title">Who sends what</p>
<p class="blog-v2-fig-sub">Routine messages should never wait behind billable work.</p>
<ol class="blog-v2-fig-cards">
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">1–4</span>
<p class="blog-v2-fig-card-title">Follow-up owner</p>
<p class="blog-v2-fig-card-body">Messages 1, 2, 3, 4. Sent on schedule. No partner approval needed.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">5</span>
<p class="blog-v2-fig-card-title">Partner</p>
<p class="blog-v2-fig-card-body">Message 5. Extension conversation, from a prepared template.</p>
</li>
<li class="blog-v2-fig-card">
<span class="blog-v2-fig-badge" aria-hidden="true">6</span>
<p class="blog-v2-fig-card-title">Whoever delivers</p>
<p class="blog-v2-fig-card-body">Message 6. Delivery and next steps, partner copied.</p>
</li>
</ol>
<p class="blog-v2-fig-foot">Four of the six are administrative. Treat them that way.</p>
</div>`,
};

function addClassToken(attrs, className) {
  const raw = String(attrs || '');
  const match = raw.match(/\sclass=(["'])([^"']*)\1/i);
  if (match) {
    const next = match[2].split(/\s+/).filter(Boolean);
    if (!next.includes(className)) next.push(className);
    return raw.replace(match[0], ` class=${match[1]}${next.join(' ')}${match[1]}`);
  }
  return `${raw} class="${className}"`;
}

function filenameFromSrc(src) {
  const value = String(src || '').split(/[?#]/)[0];
  const parts = value.split('/');
  return parts[parts.length - 1] || '';
}

export function enhanceFiguresForMobile(html) {
  return String(html || '').replace(/<figure\b([^>]*)>([\s\S]*?)<\/figure>/gi, (full, attrs, inner) => {
    const alreadyEnhanced = /\bblog-v2-figure-reflow\b/.test(inner);
    const imgMatch = inner.match(/<img\b([^>]*)>/i);
    const srcMatch = imgMatch && imgMatch[1].match(/\ssrc=(["'])([^"']*)\1/i);
    const filename = srcMatch ? filenameFromSrc(srcMatch[2]) : '';
    const recipe = alreadyEnhanced ? '' : (FIGURE_REFLOW_RECIPES[filename] || '');
    const hasReflow = alreadyEnhanced || Boolean(recipe);

    let nextInner = inner;
    if (imgMatch && hasReflow) {
      nextInner = nextInner.replace(/<img\b([^>]*)>/i, `<img${addClassToken(imgMatch[1], 'blog-v2-figure-desktop')}>`);
    }
    if (recipe) {
      if (/<figcaption\b/i.test(nextInner)) {
        nextInner = nextInner.replace(/<figcaption\b/i, `${recipe}<figcaption`);
      } else {
        nextInner += recipe;
      }
    }

    return `<figure${addClassToken(attrs, 'blog-v2-figure')}>${nextInner}</figure>`;
  });
}

function relatedHtml(relatedPosts) {
  if (!relatedPosts.length) return '';
  const items = relatedPosts
    .map((post, index) => {
      const href = `/blog/${post.slug}/`;
      const extra = index === 0 ? '' : ' pt-4 border-t border-surface-variant';
      return `<li class="${extra.trim()}">
<a class="group block" href="${escapeAttr(href)}">
<p class="font-headline-md text-headline-md text-base text-primary group-hover:text-accent-link transition-colors">${escapeHtml(post.title.replace(/_/g, ':'))}</p>
</a>
</li>`;
    })
    .join('\n');
  return `<div class="p-6 bg-surface-bright rounded-xl border border-surface-variant">
<p class="font-label-caps text-label-caps text-text-muted mb-4">Related Reading</p>
<ul class="list-none space-y-4">${items}</ul>
</div>`;
}

function sidebarHtml(meta, relatedPosts) {
  const variant = SIDEBAR_VARIANTS[meta.sidebarProduct];
  if (!variant) {
    throw new Error(`Unknown sidebar_product "${meta.sidebarProduct}"`);
  }
  return `<aside class="lg:col-span-3">
<div class="sticky top-24 space-y-8">
<div class="p-6 bg-surface-container-lowest border border-surface-variant rounded-xl shadow-[0_4px_20px_rgba(0,31,84,0.05)]">
<p class="font-headline-md text-headline-md text-primary text-xl mb-2 font-bold">${escapeHtml(variant.heading)}</p>
<p class="font-body-md text-body-md text-on-surface-variant text-sm mb-6">${escapeHtml(variant.body)}</p>
<a class="block text-center w-full border-2 border-primary text-primary px-4 py-2 rounded font-label-caps text-label-caps hover:bg-surface-variant transition-colors mb-3" href="${escapeAttr(variant.featuresHref)}">View Features</a>
<a class="block text-center w-full bg-accent-link text-white px-4 py-2 rounded font-label-caps text-label-caps hover:bg-secondary transition-colors" href="/schedule-demo">Schedule Demo</a>
</div>
${relatedHtml(relatedPosts)}
</div>
</aside>`;
}

export function formatDateTimeAttribute(isoValue) {
  const value = String(isoValue || '');
  return value.length >= 10 ? value.slice(0, 10) : value;
}

export function renderV2ArticleHtml({
  post,
  meta,
  relatedPosts,
  bodyHtml,
  imagePath,
  publishedIso,
  publishedLabel,
}) {
  const chip = meta.category
    ? `<div data-category-chip class="inline-block px-3 py-1 bg-surface-container-low text-accent-link font-label-caps text-label-caps rounded-full mb-6">${escapeHtml(meta.category)}</div>`
    : '';
  const timeAttr = formatDateTimeAttribute(publishedIso);
  const byline = publishedIso
    ? `<div class="flex items-center justify-center gap-4 text-text-muted font-body-md text-body-md"><span>AmitaSoft</span><span aria-hidden="true">·</span><time datetime="${escapeAttr(timeAttr)}">Published ${escapeHtml(publishedLabel)}</time></div>`
    : '';
  const heroImg = imagePath
    ? `<div class="max-w-container-max mx-auto mb-section-gap rounded-xl overflow-hidden shadow-sm border border-surface-variant"><img class="w-full h-auto object-cover aspect-[1.90]" src="${escapeAttr(imagePath)}" alt="${escapeAttr(post.imageAlt || post.title)}" /></div>`
    : '';

  return `<div class="w-full max-w-container-max mx-auto px-margin-base md:px-gutter pt-section-gap pb-stack-lg grid grid-cols-1 lg:grid-cols-12 gap-gutter">
<article class="contents">
<div class="lg:col-span-12">
<div class="max-w-article-max mx-auto mb-stack-lg text-center">
${chip}
<h1 class="font-headline-display text-headline-display-mobile md:text-headline-display text-primary mb-6">${escapeHtml(post.title)}</h1>
${byline}
</div>
${heroImg}
</div>
<div class="lg:col-span-8 lg:col-start-2 article-content">
${bodyHtml}
</div>
</article>
${sidebarHtml(meta, relatedPosts)}
</div>`;
}
